import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Badge, Alert, Tab, Tabs, Table, InputGroup, Spinner, Toast, Dropdown, ProgressBar } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db, functions } from '../firebase';
import { collection, getDocs, getDoc, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy, serverTimestamp, Timestamp, arrayUnion } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import TemplateReviewPanel from '../components/TemplateReviewPanel';
import TemplateSubmissionTracker from '../components/TemplateSubmissionTracker';
import GoogleMeetLinks from '../components/GoogleMeetLinks';
import LearnerNavbar from '../components/LearnerNavbar';
import AdminNavbar from '../components/AdminNavbar';
import FacilitatorNavbar from '../components/FacilitatorNavbar';
import QANavbar from '../components/QANavbar';
import templateSubmissionService from '../services/templateSubmissionService';
import { normalizeRole, isExternalFacilitatorRole, isExternalViewerRole } from '../services/roleManagementService';
import '../styles/brand.css';
import { UI_TIMEOUTS } from '../constants/ui';
import { getProtectedFileUrl, getStoragePathFromUrl } from '../services/protectedFileService';

const normalizeResource = (id, data = {}) => {
  const uploadedAtValue = data.uploadedAt?.toDate?.() || data.uploadedAt;
  const uploadedAt = uploadedAtValue ? new Date(uploadedAtValue) : new Date();

  return {
    ...data,
    id,
    title: String(data.title || data.name || 'Untitled resource'),
    description: String(data.description || ''),
    tags: Array.isArray(data.tags) ? data.tags : [],
    rating: Number.isFinite(Number(data.rating)) ? Number(data.rating) : 0,
    ratingCount: Number.isFinite(Number(data.ratingCount)) ? Number(data.ratingCount) : 0,
    downloadCount: Number.isFinite(Number(data.downloadCount)) ? Number(data.downloadCount) : 0,
    assignedExternalUsers: Array.isArray(data.assignedExternalUsers) ? data.assignedExternalUsers : [],
    uploadedAt: Number.isNaN(uploadedAt.getTime()) ? new Date() : uploadedAt,
    availableAt: data.availableAt?.toDate?.() || data.availableAt || null
  };
};

const ResourceHub = () => {
  const { currentUser, userProfile } = useAuth();
  const role = normalizeRole(userProfile?.role);
  const isExternalFacilitator = isExternalFacilitatorRole(role);
  const isExternalViewer = isExternalViewerRole(role);
  const [activeTab, setActiveTab] = useState('browse');
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modules, setModules] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showSubmissionRequestModal, setShowSubmissionRequestModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [showTemplateSubmitModal, setShowTemplateSubmitModal] = useState(false);
  const [templateSubmitResource, setTemplateSubmitResource] = useState(null);
  const [templateSubmitFile, setTemplateSubmitFile] = useState(null);
  const [templateSubmitComments, setTemplateSubmitComments] = useState('');
  const [templateSubmitProgress, setTemplateSubmitProgress] = useState(0);
  const [templateSubmitting, setTemplateSubmitting] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  // Program and cohort filtering
  const [programs, setPrograms] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState('all');
  const [selectedCohort, setSelectedCohort] = useState('all');
  const [submissionRequestProgram, setSubmissionRequestProgram] = useState('');
  const [submissionRequestCohorts, setSubmissionRequestCohorts] = useState([]);
  const [submissionRequestForm, setSubmissionRequestForm] = useState({
    title: '',
    dueDate: '',
    requiredFileType: 'pdf',
    allowMultipleFiles: true,
    maxFiles: ''
  });

  // External users for resource assignment (admin only)
  const [externalUsers, setExternalUsers] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [resourceToAssign, setResourceToAssign] = useState(null);
  const [trackingResource, setTrackingResource] = useState(null);
  const [selectedExternalUsers, setSelectedExternalUsers] = useState([]);
  const [submissionRequestsList, setSubmissionRequestsList] = useState([]);
  const [selectedSubmissionRequest, setSelectedSubmissionRequest] = useState(null);
  const [submissionRequestSubmissions, setSubmissionRequestSubmissions] = useState([]);
  const [loadingSubmissionRequests, setLoadingSubmissionRequests] = useState(false);
  const [loadingSubmissionSubmissions, setLoadingSubmissionSubmissions] = useState(false);
  const [submissionRequestLearners, setSubmissionRequestLearners] = useState([]);
  const [submissionMissingLearners, setSubmissionMissingLearners] = useState([]);
  const [showSubmissionStatusModal, setShowSubmissionStatusModal] = useState(false);
  const [submissionStatusForm, setSubmissionStatusForm] = useState({ status: 'submitted', comment: '' });
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showSubmissionEditModal, setShowSubmissionEditModal] = useState(false);
  const [submissionEditForm, setSubmissionEditForm] = useState({
    dueDate: '',
    status: 'open',
    allowMultipleFiles: true,
    maxFiles: ''
  });

  // Upload form data
  const [uploadFormData, setUploadFormData] = useState({
    title: '',
    description: '',
    category: 'documents',
    type: 'pdf',
    tags: '',
    accessLevel: 'public',
    moduleId: '',
    releaseType: 'immediate',
    releaseMeetingId: '',
    releaseDelayMinutes: 5,
    releaseAt: '',
    file: null,
    url: '',
    isExternalLink: false,
    isFillableTemplate: false,
    templateInstructions: '',
    templateDueDate: '',
    programId: '',
    cohortId: '',
    cohortIds: [],
    assignedExternalUsers: []
  });

  // Resource categories and types
  const resourceCategories = [
    { value: 'documents', label: 'Documents & PDFs', icon: '' },
    { value: 'videos', label: 'Videos & Tutorials', icon: '' },
    { value: 'links', label: 'External Links', icon: '' },
    { value: 'templates', label: 'Templates & Forms', icon: '' },
    { value: 'tools', label: 'Tools & Software', icon: '' },
    { value: 'assessments', label: 'Assessments & Quizzes', icon: '' },
    { value: 'presentations', label: 'Presentations', icon: '' },
    { value: 'research', label: 'Research & Articles', icon: '' }
  ];

  const resourceTypes = [
    { value: 'pdf', label: 'PDF Document' },
    { value: 'video', label: 'Video' },
    { value: 'audio', label: 'Audio' },
    { value: 'image', label: 'Image' },
    { value: 'document', label: 'Document' },
    { value: 'spreadsheet', label: 'Spreadsheet' },
    { value: 'presentation', label: 'Presentation' },
    { value: 'link', label: 'External Link' },
    { value: 'tool', label: 'Software/Tool' },
    { value: 'template', label: 'Template' }
  ];

  const resourceSectionDefinitions = [
    { key: 'recordings', label: 'Recordings & Videos' },
    { key: 'assignments', label: 'Assignments & Assessments' },
    { key: 'notes', label: 'Notes, Documents & Research' },
    { key: 'presentations', label: 'Presentations' },
    { key: 'templates', label: 'Templates & Forms' },
    { key: 'links', label: 'Links & Tools' },
    { key: 'other', label: 'Other Resources' }
  ];

  useEffect(() => {
    if (!currentUser || !userProfile) {
      setLoading(false);
      return;
    }

    // External viewers are reporting-only: no access to resource hub.
    if (isExternalViewer) {
      setLoading(false);
      return;
    }

    loadResources();
    loadCategories();
    loadPrograms();
    loadCohorts();
    loadModules();
    if (role === 'admin') {
      loadExternalUsers();
    }
  }, [currentUser, userProfile, role, isExternalViewer]);

  const loadPrograms = async () => {
    try {
      const programsRef = collection(db, 'programs');

      if (role === 'admin' || role === 'qa' || !isExternalFacilitator) {
        const snapshot = await getDocs(programsRef);
        const fetchedPrograms = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPrograms(fetchedPrograms);
        return;
      }

      // External facilitator: can only read assigned programmes (rules-enforced).
      const assignedPrograms = Array.isArray(userProfile?.assignedPrograms) ? userProfile.assignedPrograms : [];
      if (!assignedPrograms.length) {
        setPrograms([]);
        return;
      }

      const chunks = [];
      for (let i = 0; i < assignedPrograms.length; i += 10) {
        chunks.push(assignedPrograms.slice(i, i + 10));
      }

      const programDocs = [];
      for (const chunk of chunks) {
        const q = query(programsRef, where('__name__', 'in', chunk));
        const snapshot = await getDocs(q);
        programDocs.push(...snapshot.docs);
      }

      setPrograms(programDocs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    } catch (error) {
      devError('Error loading programs:', error);
    }
  };

  const loadCohorts = async () => {
    try {
      const cohortsRef = collection(db, 'cohorts');

      // Learners don't have list-all-cohorts permission — fetch only their own cohort(s)
      if (role === 'learner') {
        const ids = [...new Set([
          userProfile?.cohortId,
          ...(Array.isArray(userProfile?.assignedCohorts) ? userProfile.assignedCohorts : []),
          ...(Array.isArray(userProfile?.cohortIds) ? userProfile.cohortIds : []),
        ].filter(Boolean))];
        if (!ids.length) { setCohorts([]); return; }
        const fetched = [];
        for (const id of ids) {
          try {
            const snap = await getDoc(doc(db, 'cohorts', id));
            if (snap.exists()) fetched.push({ id: snap.id, ...snap.data() });
          } catch { /* individual doc denied — skip */ }
        }
        setCohorts(fetched);
        return;
      }

      if (role === 'admin' || role === 'qa' || !isExternalFacilitator) {
        const snapshot = await getDocs(cohortsRef);
        const fetchedCohorts = snapshot.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        setCohorts(fetchedCohorts);
        return;
      }

      // External facilitator: can only read assigned cohorts or cohorts under assigned programmes.
      const assignedCohorts = Array.isArray(userProfile?.assignedCohorts) ? userProfile.assignedCohorts : [];
      const assignedPrograms = Array.isArray(userProfile?.assignedPrograms) ? userProfile.assignedPrograms : [];

      const cohortDocs = [];
      if (assignedCohorts.length) {
        const chunks = [];
        for (let i = 0; i < assignedCohorts.length; i += 10) {
          chunks.push(assignedCohorts.slice(i, i + 10));
        }
        for (const chunk of chunks) {
          const q = query(cohortsRef, where('__name__', 'in', chunk));
          const snapshot = await getDocs(q);
          cohortDocs.push(...snapshot.docs);
        }
      } else if (assignedPrograms.length) {
        const chunks = [];
        for (let i = 0; i < assignedPrograms.length; i += 10) {
          chunks.push(assignedPrograms.slice(i, i + 10));
        }
        for (const chunk of chunks) {
          const q = query(cohortsRef, where('programId', 'in', chunk));
          const snapshot = await getDocs(q);
          cohortDocs.push(...snapshot.docs);
        }
      }

      setCohorts(cohortDocs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    } catch (error) {
      devError('Error loading cohorts:', error);
    }
  };

  const loadExternalUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('role', 'in', [
        'external',
        'external_facilitator',
        'external_viewer',
        'external_facilitator_limited',
        'external_viewer_reporting'
      ]));
      const snapshot = await getDocs(q);
      const fetchedUsers = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setExternalUsers(fetchedUsers);
    } catch (error) {
      devError('Error loading external users:', error);
    }
  };

  const loadResources = async () => {
    try {
      setLoading(true);
      if (!currentUser || !userProfile) {
        setResources([]);
        return;
      }

      const resourcesRef = collection(db, 'resources');
      const queryAttempts = [
        query(resourcesRef, orderBy('uploadedAt', 'desc')),
        query(resourcesRef, where('accessLevel', 'in', ['public', 'all', 'learner', 'learners'])),
        query(resourcesRef, where('visibilityScope', '==', 'all_applicants'), where('active', '==', true))
      ];

      let snapshot = null;
      let lastError = null;
      for (const resourceQuery of queryAttempts) {
        try {
          snapshot = await getDocs(resourceQuery);
          break;
        } catch (queryError) {
          lastError = queryError;
          devLog('Resource query attempt failed, trying fallback:', queryError);
        }
      }

      if (!snapshot) {
        throw lastError || new Error('Resource query failed');
      }
      let fetchedResources = snapshot.docs.map(doc => normalizeResource(doc.id, doc.data()));

      setResources(fetchedResources);
    } catch (error) {
      devError('Error loading resources:', error);
      showToastMessage('Error loading resources', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = () => {
    // Set available categories
    setCategories(resourceCategories);
  };

  const loadModules = async () => {
    try {
      const modulesSnapshot = await getDocs(collection(db, 'modules'));
      let modulesData = modulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (role === 'learner' && userProfile?.programId) {
        modulesData = modulesData.filter(module => module.programId === userProfile.programId);
      } else if (role !== 'admin' && role !== 'qa') {
        const assignedPrograms = userProfile?.assignedPrograms || [];
        if (assignedPrograms.length > 0) {
          modulesData = modulesData.filter(module => assignedPrograms.includes(module.programId));
        }
      }

      setModules(modulesData);
    } catch (error) {
      devError('Error loading modules:', error);
      setModules([]);
    }
  };


  const showToastMessage = (message, variant = 'success') => {
    setToastMessage(message);
    // Bootstrap Toast bg prop uses 'danger' not 'error'
    setToastVariant(variant === 'error' ? 'danger' : variant);
    setShowToast(true);
    setTimeout(() => setShowToast(false), UI_TIMEOUTS.TOAST_DISMISS_MS);
  };

  const toggleSubmissionRequestCohort = (cohortId) => {
    setSubmissionRequestCohorts((prev) => {
      if (prev.includes(cohortId)) {
        return prev.filter((id) => id !== cohortId);
      }
      return [...prev, cohortId];
    });
  };

  const handleCreateSubmissionRequest = async () => {
    if (!submissionRequestForm.title.trim()) {
      showToastMessage('Please enter a request title.', 'error');
      return;
    }
    if (!submissionRequestForm.dueDate) {
      showToastMessage('Please select a due date.', 'error');
      return;
    }
    if (submissionRequestCohorts.length === 0) {
      showToastMessage('Please select at least one cohort.', 'error');
      return;
    }

    try {
      const dueDate = new Date(`${submissionRequestForm.dueDate}T23:59:59`);
      const allowMultipleFiles = submissionRequestForm.allowMultipleFiles !== false;
      const parsedMaxFiles = Number(submissionRequestForm.maxFiles);
      const maxFiles = allowMultipleFiles && Number.isFinite(parsedMaxFiles) && parsedMaxFiles > 0
        ? parsedMaxFiles
        : (allowMultipleFiles ? null : 1);
      await addDoc(collection(db, 'submissionRequests'), {
        title: submissionRequestForm.title.trim(),
        dueDate: Timestamp.fromDate(dueDate),
        requiredFileType: submissionRequestForm.requiredFileType,
        allowMultipleFiles,
        maxFiles,
        cohortIds: submissionRequestCohorts,
        programId: submissionRequestProgram || null,
        createdById: currentUser?.uid || null,
        createdByName: userProfile?.displayName || userProfile?.email || 'Facilitator',
        status: 'open',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      showToastMessage('Submission request created.', 'success');
      setShowSubmissionRequestModal(false);
      setSubmissionRequestForm({
        title: '',
        dueDate: '',
        requiredFileType: 'pdf',
        allowMultipleFiles: true,
        maxFiles: ''
      });
      setSubmissionRequestCohorts([]);
      setSubmissionRequestProgram('');
    } catch (error) {
      devError('Error creating submission request:', error);
      showToastMessage('Failed to create submission request.', 'error');
    }
  };

  const computeAvailableAt = () => {
    if (uploadFormData.releaseType === 'scheduled' && uploadFormData.releaseAt) {
      const scheduled = new Date(uploadFormData.releaseAt);
      if (!Number.isNaN(scheduled.getTime())) {
        return scheduled;
      }
    }

    return null;
  };

  useEffect(() => {
    if (showUploadModal) {
      loadModules();
    }
  }, [showUploadModal]);

  useEffect(() => {
    // Submission-request workflows are internal-only for now.
    if (['facilitator', 'admin', 'qa'].includes(role)) {
      loadSubmissionRequests();
    }
  }, [role, userProfile?.assignedCohorts?.length]);

  useEffect(() => {
    if (selectedSubmissionRequest?.id) {
      loadSubmissionSubmissions(selectedSubmissionRequest.id);
    }
  }, [selectedSubmissionRequest?.id]);

  useEffect(() => {
    if (selectedSubmissionRequest?.id) {
      loadSubmissionRequestLearners(selectedSubmissionRequest);
    } else {
      setSubmissionRequestLearners([]);
      setSubmissionMissingLearners([]);
    }
  }, [selectedSubmissionRequest?.id]);

  useEffect(() => {
    if (!submissionRequestLearners.length) {
      setSubmissionMissingLearners([]);
      return;
    }
    const submittedIds = new Set(
      submissionRequestSubmissions
        .flatMap((submission) => [
          submission.learnerId,
          submission.userId,
          submission.submittedById,
          submission.id
        ].filter(Boolean))
    );
    const missing = submissionRequestLearners.filter((learner) => !submittedIds.has(learner.id));
    setSubmissionMissingLearners(missing);
  }, [submissionRequestLearners, submissionRequestSubmissions]);

  const openTemplateSubmitModal = (resource) => {
    setTemplateSubmitResource(resource);
    setTemplateSubmitFile(null);
    setTemplateSubmitComments('');
    setTemplateSubmitProgress(0);
    setShowTemplateSubmitModal(true);
  };

  const loadSubmissionRequests = async () => {
    try {
      setLoadingSubmissionRequests(true);
      const requestsRef = collection(db, 'submissionRequests');
      const activeRole = role;

      let requests = [];
      if (activeRole === 'admin' || activeRole === 'qa') {
        const snapshot = await getDocs(query(requestsRef, orderBy('createdAt', 'desc')));
        requests = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      } else if (activeRole === 'facilitator') {
        const snapshots = [];
        snapshots.push(getDocs(query(requestsRef, where('createdById', '==', currentUser?.uid))));

        const assignedCohorts = Array.isArray(userProfile?.assignedCohorts) ? userProfile.assignedCohorts : [];
        if (assignedCohorts.length) {
          for (let i = 0; i < assignedCohorts.length; i += 10) {
            const chunk = assignedCohorts.slice(i, i + 10);
            snapshots.push(getDocs(query(requestsRef, where('cohortIds', 'array-contains-any', chunk))));
          }
        }

        const results = await Promise.all(snapshots);
        const merged = new Map();
        results.forEach((snap) => {
          snap.docs.forEach((docSnap) => merged.set(docSnap.id, { id: docSnap.id, ...docSnap.data() }));
        });
        requests = Array.from(merged.values());
      }

      requests.sort((a, b) => {
        const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
        const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
        return bTime - aTime;
      });

      setSubmissionRequestsList(requests);
      if (requests.length) {
        const currentId = selectedSubmissionRequest?.id;
        const stillExists = currentId && requests.some(req => req.id === currentId);
        if (!stillExists) {
          setSelectedSubmissionRequest(requests[0]);
        }
      } else {
        setSelectedSubmissionRequest(null);
      }
    } catch (error) {
      devError('Error loading submission requests:', error);
    } finally {
      setLoadingSubmissionRequests(false);
    }
  };

  const loadSubmissionSubmissions = async (requestId) => {
    if (!requestId) return;
    try {
      setLoadingSubmissionSubmissions(true);
      const submissionsRef = collection(db, 'submissionRequests', requestId, 'submissions');
      const snapshot = await getDocs(query(submissionsRef, orderBy('submittedAt', 'desc')));
      const submissions = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        const submittedAt = data.submittedAt?.toDate ? data.submittedAt.toDate() : (data.submittedAt ? new Date(data.submittedAt) : null);
        return {
          id: docSnap.id,
          ...data,
          learnerId: data.learnerId || data.userId || data.submittedById || docSnap.id,
          status: data.status || 'submitted',
          submittedAt
        };
      });
      setSubmissionRequestSubmissions(submissions);
    } catch (error) {
      devError('Error loading submission request submissions:', error);
    } finally {
      setLoadingSubmissionSubmissions(false);
    }
  };

  const loadSubmissionRequestLearners = async (request) => {
    try {
      const cohortIds = Array.isArray(request?.cohortIds) ? request.cohortIds.filter(Boolean) : [];
      if (!cohortIds.length) {
        setSubmissionRequestLearners([]);
        return;
      }

      const isActiveLearner = (learner) => {
        if (!learner) return false;
        if (learner.deleted || learner.deactivated) return false;
        if (learner.isActive === false) return false;
        const status = (learner.status || '').toLowerCase();
        if (['inactive', 'dropoff', 'dropped'].includes(status)) return false;
        return true;
      };

      const learners = [];
      const learnerRoles = ['learner', 'applicant', 'student', 'graduate', 'candidate'];
      for (let i = 0; i < cohortIds.length; i += 10) {
        const chunk = cohortIds.slice(i, i + 10);
        const snapshot = await getDocs(
          query(collection(db, 'users'), where('cohortId', 'in', chunk))
        );
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          if (!learnerRoles.includes(data.role)) return;
          const learner = {
            id: docSnap.id,
            displayName: data.displayName || data.name || data.email || 'Learner',
            email: data.email || '',
            cohortId: data.cohortId || '',
            status: data.status,
            isActive: data.isActive,
            deleted: data.deleted,
            deactivated: data.deactivated
          };
          if (isActiveLearner(learner)) {
            learners.push(learner);
          }
        });
      }

      setSubmissionRequestLearners(learners);
    } catch (error) {
      devError('Error loading submission request learners:', error);
      setSubmissionRequestLearners([]);
    }
  };

  const openSubmissionStatusModal = (submission) => {
    setSelectedSubmission(submission);
    setSubmissionStatusForm({
      status: submission.status || 'submitted',
      comment: ''
    });
    setShowSubmissionStatusModal(true);
  };

  const openResubmissionRequestModal = (submission) => {
    setSelectedSubmission(submission);
    setSubmissionStatusForm({
      status: 'revision_requested',
      comment: ''
    });
    setShowSubmissionStatusModal(true);
  };

  const handleUpdateSubmissionStatus = async () => {
    if (!selectedSubmissionRequest || !selectedSubmission) return;
    try {
      await updateDoc(
        doc(db, 'submissionRequests', selectedSubmissionRequest.id, 'submissions', selectedSubmission.id),
        {
          status: submissionStatusForm.status,
          latestComment: submissionStatusForm.comment || '',
          reviewHistory: arrayUnion({
            status: submissionStatusForm.status,
            comment: submissionStatusForm.comment || '',
            reviewerId: currentUser?.uid || '',
            reviewedAt: new Date()
          }),
          reviewedAt: serverTimestamp()
        }
      );

      if (selectedSubmission.learnerId) {
        const NotificationService = (await import('../services/notificationService')).default;
        await NotificationService.notify(
          'submission_status',
          'Submission Status Updated',
          `Your submission for "${selectedSubmissionRequest.title}" was marked as ${submissionStatusForm.status}.`,
          selectedSubmission.learnerId,
          { category: 'submission_requests', additionalData: { requestId: selectedSubmissionRequest.id } }
        );
      }

      setShowSubmissionStatusModal(false);
      setSubmissionStatusForm({ status: 'submitted', comment: '' });
      setSelectedSubmission(null);
      loadSubmissionSubmissions(selectedSubmissionRequest.id);
    } catch (error) {
      devError('Error updating submission status:', error);
      showToastMessage('Failed to update submission status.', 'error');
    }
  };

  const handleDeleteSubmission = async (submissionId) => {
    if (!selectedSubmissionRequest) return;
    const confirmation = window.prompt('Type DELETE to confirm submission deletion.');
    if (confirmation !== 'DELETE') return;
    try {
      await deleteDoc(doc(db, 'submissionRequests', selectedSubmissionRequest.id, 'submissions', submissionId));
      loadSubmissionSubmissions(selectedSubmissionRequest.id);
    } catch (error) {
      devError('Error deleting submission:', error);
      showToastMessage('Failed to delete submission.', 'error');
    }
  };

  const sanitizeFilePart = (value) =>
    String(value || '')
      .trim()
      .replace(/[<>:"/\\|?*]+/g, '_')
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .slice(0, 80);

  const openSubmissionFilePreview = (fileUrl, fileName = '') => {
    if (!fileUrl) return;
    const lowerName = String(fileName || '').toLowerCase();
    if (lowerName.endsWith('.doc') || lowerName.endsWith('.docx')) {
      const officePreview = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`;
      window.open(officePreview, '_blank', 'noopener,noreferrer');
      return;
    }
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  };

  const handleBulkDownloadSubmissionFiles = async () => {
    if (!selectedSubmissionRequest || submissionRequestSubmissions.length === 0) return;
    const filesToDownload = [];
    submissionRequestSubmissions.forEach((submission) => {
      const learnerName = sanitizeFilePart(submission.learnerName || 'Learner');
      const learnerId = sanitizeFilePart(submission.learnerId || 'NoID');
      const requestTitle = sanitizeFilePart(selectedSubmissionRequest.title || 'SubmissionRequest');

      if (Array.isArray(submission.files) && submission.files.length > 0) {
        submission.files.forEach((file, index) => {
          filesToDownload.push({
            url: file.fileUrl || file.url,
            fileName: `${learnerName}_${learnerId}_${requestTitle}_${sanitizeFilePart(file.fileName || `file_${index + 1}`)}`
          });
        });
      } else if (submission.fileUrl) {
        filesToDownload.push({
          url: submission.fileUrl,
          fileName: `${learnerName}_${learnerId}_${requestTitle}_${sanitizeFilePart(submission.fileName || 'submission_file')}`
        });
      }
    });

    if (!filesToDownload.length) {
      showToastMessage('No submission files found to download.', 'warning');
      return;
    }

    for (const file of filesToDownload) {
      const link = document.createElement('a');
      link.href = file.url;
      link.download = file.fileName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, UI_TIMEOUTS.SIMULATED_DELAY_MS));
    }

    showToastMessage(`Triggered download for ${filesToDownload.length} file(s).`, 'success');
  };

  const handleSendSubmissionReminder = async () => {
    if (!selectedSubmissionRequest || submissionMissingLearners.length === 0) return;
    try {
      const sendCustomEmail = httpsCallable(functions, 'sendCustomEmail');
      const subject = `Reminder: ${selectedSubmissionRequest.title} submission`;
      const dueDate = selectedSubmissionRequest.dueDate?.toDate
        ? selectedSubmissionRequest.dueDate.toDate().toLocaleDateString()
        : (selectedSubmissionRequest.dueDate ? new Date(selectedSubmissionRequest.dueDate).toLocaleDateString() : 'N/A');
      const htmlContent = `
        <p>Hello {name},</p>
        <p>This is a reminder to submit: <strong>${selectedSubmissionRequest.title}</strong>.</p>
        <p>Due date: <strong>${dueDate}</strong>.</p>
        <p>Please log in to the LMS to upload your submission.</p>
      `;
      const recipients = submissionMissingLearners
        .map((learner) => learner.email)
        .filter(Boolean);
      await sendCustomEmail({
        subject,
        htmlContent,
        textContent: '',
        recipients
      });
      showToastMessage('Reminder emails sent.', 'success');
    } catch (error) {
      devError('Error sending reminders:', error);
      showToastMessage('Failed to send reminders.', 'error');
    }
  };

  const openSubmissionEditModal = () => {
    if (!selectedSubmissionRequest) return;
    const dueDateValue = selectedSubmissionRequest.dueDate?.toDate
      ? selectedSubmissionRequest.dueDate.toDate().toISOString().split('T')[0]
      : (selectedSubmissionRequest.dueDate ? new Date(selectedSubmissionRequest.dueDate).toISOString().split('T')[0] : '');
    setSubmissionEditForm({
      dueDate: dueDateValue,
      status: selectedSubmissionRequest.status || 'open',
      allowMultipleFiles: selectedSubmissionRequest.allowMultipleFiles !== false,
      maxFiles: selectedSubmissionRequest.maxFiles ? String(selectedSubmissionRequest.maxFiles) : ''
    });
    setShowSubmissionEditModal(true);
  };

  const handleUpdateSubmissionRequest = async () => {
    if (!selectedSubmissionRequest) return;
    try {
      const allowMultipleFiles = submissionEditForm.allowMultipleFiles !== false;
      const parsedMaxFiles = Number(submissionEditForm.maxFiles);
      const maxFiles = allowMultipleFiles && Number.isFinite(parsedMaxFiles) && parsedMaxFiles > 0
        ? parsedMaxFiles
        : (allowMultipleFiles ? null : 1);
      const updates = {
        status: submissionEditForm.status || 'open',
        allowMultipleFiles,
        maxFiles,
        updatedAt: serverTimestamp()
      };
      if (submissionEditForm.dueDate) {
        updates.dueDate = Timestamp.fromDate(new Date(`${submissionEditForm.dueDate}T23:59:59`));
      }
      await updateDoc(doc(db, 'submissionRequests', selectedSubmissionRequest.id), updates);
      setShowSubmissionEditModal(false);
      await loadSubmissionRequests();
    } catch (error) {
      devError('Error updating submission request:', error);
      showToastMessage('Failed to update submission request.', 'error');
    }
  };

  const handleDeleteSubmissionRequest = async () => {
    if (!selectedSubmissionRequest) return;
    if (!window.confirm('Delete this submission request? This cannot be undone.')) {
      return;
    }
    try {
      await deleteDoc(doc(db, 'submissionRequests', selectedSubmissionRequest.id));
      setSelectedSubmissionRequest(null);
      setSubmissionRequestSubmissions([]);
      await loadSubmissionRequests();
    } catch (error) {
      devError('Error deleting submission request:', error);
      showToastMessage('Failed to delete submission request.', 'error');
    }
  };

  const handleTemplateSubmit = async (event) => {
    event.preventDefault();
    if (!templateSubmitResource || !templateSubmitFile || !currentUser) {
      showToastMessage('Please select a file to upload.', 'error');
      return;
    }

    try {
      setTemplateSubmitting(true);
      const dueDate = templateSubmitResource.templateDueDate?.toDate
        ? templateSubmitResource.templateDueDate.toDate()
        : templateSubmitResource.templateDueDate || null;
      await templateSubmissionService.submitTemplate(
        {
          templateId: templateSubmitResource.id,
          templateTitle: templateSubmitResource.title,
          learnerId: currentUser.uid,
          learnerName: userProfile?.displayName || currentUser.email,
          comments: templateSubmitComments,
          dueDate
        },
        templateSubmitFile,
        (progress) => setTemplateSubmitProgress(progress)
      );
      showToastMessage('Template submitted successfully!', 'success');
      setShowTemplateSubmitModal(false);
    } catch (error) {
      devError('Error submitting template:', error);
      showToastMessage(error.message || 'Failed to submit template.', 'error');
    } finally {
      setTemplateSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return null;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const k = 1024;
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
    const value = bytes / Math.pow(k, i);
    return `${value.toFixed(2)} ${sizes[i]}`;
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!uploadFormData.title.trim()) {
        showToastMessage('Please enter a resource title', 'error');
        return;
      }

      if (!uploadFormData.isExternalLink && !uploadFormData.file) {
        showToastMessage('Please select a file to upload', 'error');
        return;
      }

      if (uploadFormData.isExternalLink && !uploadFormData.url.trim()) {
        showToastMessage('Please enter a valid URL', 'error');
        return;
      }

      if (uploadFormData.releaseType === 'scheduled' && !uploadFormData.releaseAt) {
        showToastMessage('Please choose a date/time for the release schedule', 'error');
        return;
      }

      setLoading(true);

      let fileSize = null;
      let storagePath = null;

      // Upload file if not external link
      if (!uploadFormData.isExternalLink && uploadFormData.file) {
        const file = uploadFormData.file;
        const maxFileSize = 1024 * 1024 * 1024; // 1GB
        if (file.size > maxFileSize) {
          showToastMessage('File is too large. Maximum file size is 1 GB.', 'error');
          return;
        }

        const { ref, uploadBytesResumable } = await import('firebase/storage');
        const { storage } = await import('../firebase');

        const fileName = `${Date.now()}_${file.name}`;
        storagePath = `lms-resources/${currentUser.uid}/${fileName}`;
        const storageRef = ref(storage, storagePath);

        setUploadingFile(true);
        setUploadProgress(0);
        const uploadTask = uploadBytesResumable(storageRef, file);

        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
              setUploadProgress(progress);
            },
            reject,
            resolve
          );
        });

        fileSize = formatFileSize(file.size);
      }

      const availableAt = computeAvailableAt();

      const resourceData = {
        title: uploadFormData.title,
        description: uploadFormData.description,
        category: uploadFormData.category,
        type: uploadFormData.type,
        tags: uploadFormData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        accessLevel: uploadFormData.accessLevel,
        moduleId: uploadFormData.moduleId || null,
        isExternalLink: uploadFormData.isExternalLink,
        url: uploadFormData.isExternalLink ? uploadFormData.url : null,
        fileURL: null,
        fileSize: fileSize,
        storagePath: storagePath,
        uploadedBy: currentUser.displayName || currentUser.email,
        uploadedById: currentUser.uid,
        uploadedAt: serverTimestamp(),
        availableAt: availableAt ? Timestamp.fromDate(availableAt) : null,
        releaseType: uploadFormData.releaseType || 'immediate',
        releaseMeetingId: null,
        releaseDelayMinutes: null,
        downloadCount: 0,
        rating: 0,
        ratingCount: 0,
        status: 'active',
        // Program and cohort association
        programId: uploadFormData.programId || null,
        cohortIds: uploadFormData.cohortIds?.length ? uploadFormData.cohortIds : null,
        cohortId: uploadFormData.cohortIds?.length === 1 ? uploadFormData.cohortIds[0] : null,
        // External user assignment
        assignedExternalUsers: uploadFormData.assignedExternalUsers || [],
        // Fillable template fields
        isFillableTemplate: uploadFormData.isFillableTemplate || false,
        templateInstructions: uploadFormData.isFillableTemplate ? uploadFormData.templateInstructions : null,
        templateDueDate: uploadFormData.isFillableTemplate && uploadFormData.templateDueDate
          ? new Date(uploadFormData.templateDueDate)
          : null,
        submissionCount: 0
      };

      await addDoc(collection(db, 'resources'), resourceData);

      showToastMessage('Resource uploaded successfully!', 'success');
      setShowUploadModal(false);
      resetUploadForm();
      loadResources(); // Refresh list

    } catch (error) {
      devError('Error uploading resource:', error);
      showToastMessage('Error uploading resource: ' + error.message, 'error');
    } finally {
      setLoading(false);
      setUploadingFile(false);
      setUploadProgress(0);
    }
  };

  const resetUploadForm = () => {
    setUploadFormData({
      title: '',
      description: '',
      category: 'documents',
      type: 'pdf',
      tags: '',
      accessLevel: 'public',
      moduleId: '',
      releaseType: 'immediate',
      releaseMeetingId: '',
      releaseDelayMinutes: 5,
      releaseAt: '',
      file: null,
      url: '',
      isExternalLink: false,
      isFillableTemplate: false,
      templateInstructions: '',
      templateDueDate: '',
      programId: '',
      cohortId: '',
      cohortIds: [],
      assignedExternalUsers: []
    });
    setUploadProgress(0);
    setUploadingFile(false);
  };

  const handleDownload = async (resource) => {
    setDownloadingId(resource.id);
    try {
      devLog('Download clicked for resource:', resource);
      devLog('Resource fileURL:', resource.fileURL);

      if (resource.isExternalLink) {
        window.open(resource.url, '_blank');
        showToastMessage(`Opening ${resource.title}...`, 'success');
      } else if (resource.storagePath || resource.fileURL) {
        const storagePath = resource.storagePath || getStoragePathFromUrl(resource.fileURL);
        if (!storagePath) {
          throw new Error('This resource does not have a valid storage path.');
        }
        const protectedUrl = await getProtectedFileUrl({
          path: storagePath,
          resourceId: resource.id
        });
        // Create a temporary link element to force download
        const link = document.createElement('a');
        link.href = protectedUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';

        // Try to trigger download attribute if same origin
        if (resource.title) {
          link.download = resource.title;
        }

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showToastMessage(`Opening ${resource.title}...`, 'success');

        // Update download count in Firestore (async, don't wait)
        if (resource.id) {
          updateDoc(doc(db, 'resources', resource.id), {
            downloadCount: (resource.downloadCount || 0) + 1
          }).catch(err => devError('Error updating download count:', err));
        }

        // Update local state
        const updatedResources = resources.map(r =>
          r.id === resource.id ? { ...r, downloadCount: (r.downloadCount || 0) + 1 } : r
        );
        setResources(updatedResources);
      } else {
        devError('No fileURL found for resource:', resource);
        showToastMessage('Download URL not found. Please contact support.', 'error');
      }

    } catch (error) {
      devError('Error downloading resource:', error);
      showToastMessage(`Error: ${error.message}`, 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleRateResource = async (resourceId, rating) => {
    try {
      // Update resource rating
      const updatedResources = resources.map(r => {
        if (r.id === resourceId) {
          const newRatingCount = r.ratingCount + 1;
          const newRating = ((r.rating * r.ratingCount) + rating) / newRatingCount;
          return { ...r, rating: newRating, ratingCount: newRatingCount };
        }
        return r;
      });
      setResources(updatedResources);
      showToastMessage('Thank you for your rating!', 'success');
    } catch (error) {
      devError('Error rating resource:', error);
      showToastMessage('Error submitting rating', 'error');
    }
  };

  const getCohortProgramId = (cohortId) => (
    cohorts.find(c => c.id === cohortId)?.programId || null
  );

  const getLearnerCohortIds = () => Array.from(new Set([
    userProfile?.cohortId,
    ...(Array.isArray(userProfile?.cohortIds) ? userProfile.cohortIds : []),
    ...(Array.isArray(userProfile?.assignedCohorts) ? userProfile.assignedCohorts : [])
  ].filter(Boolean)));

  const getLearnerProgramIds = () => Array.from(new Set([
    userProfile?.programId,
    ...(Array.isArray(userProfile?.programIds) ? userProfile.programIds : []),
    ...getLearnerCohortIds().map(getCohortProgramId)
  ].filter(Boolean)));

  const resourceMatchesProgram = (resource, programId) => {
    if (!programId) return true;
    if (resource.programId) return resource.programId === programId;
    const ids = Array.isArray(resource.cohortIds)
      ? resource.cohortIds
      : (resource.cohortId ? [resource.cohortId] : []);
    if (ids.length === 0) return true;
    return ids.some(id => getCohortProgramId(id) === programId);
  };

  const resourceVisibleToLearner = (resource) => {
    const now = new Date();
    if (resource.availableAt) {
      const availableAt = resource.availableAt?.toDate
        ? resource.availableAt.toDate()
        : new Date(resource.availableAt);
      if (availableAt > now) return false;
    }
    const learnerCohortIds = getLearnerCohortIds();
    const learnerProgramIds = getLearnerProgramIds();
    const resourceCohortIds = Array.isArray(resource.cohortIds)
      ? resource.cohortIds.filter(Boolean)
      : (resource.cohortId ? [resource.cohortId] : []);

    if (resource.category === 'templates') {
      if (learnerCohortIds.length === 0) return false;
      if (!resourceCohortIds.some((cohortId) => learnerCohortIds.includes(cohortId))) return false;
      if (resource.programId && !learnerProgramIds.includes(resource.programId)) return false;
      return true;
    }

    if (
      resourceCohortIds.length > 0 &&
      !resourceCohortIds.some((cohortId) => learnerCohortIds.includes(cohortId))
    ) {
      return false;
    }
    if (resource.programId && !learnerProgramIds.includes(resource.programId)) return false;
    return true;
  };

  const filteredResources = resources.filter(resource => {
    const normalizedSearch = String(searchTerm || '').toLowerCase();
    const title = String(resource?.title || '');
    const description = String(resource?.description || '');
    const tags = Array.isArray(resource?.tags) ? resource.tags : [];
    const matchesSearch = title.toLowerCase().includes(normalizedSearch) ||
      description.toLowerCase().includes(normalizedSearch) ||
      tags.some(tag => String(tag || '').toLowerCase().includes(normalizedSearch));

    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesType = selectedType === 'all' || resource.type === selectedType;

    // Program and cohort filtering
    const matchesProgram = role === 'learner'
      ? resourceVisibleToLearner(resource)
      : (selectedProgram === 'all' || !resource.programId || resource.programId === selectedProgram);
    const matchesCohort = role === 'learner'
      ? true
      : (selectedCohort === 'all' || resourceMatchesCohort(resource, selectedCohort));

    // Access level filtering
    const normalizedAccess = resource.accessLevel || 'public';
    let hasAccess = normalizedAccess === 'public' ||
      normalizedAccess === 'all' ||
      normalizedAccess === 'learner' ||
      normalizedAccess === 'learners' ||
      (role === 'learner' && ['module', 'cohort', 'program'].includes(normalizedAccess)) ||
      (resource.accessLevel === 'facilitators' && ['facilitator', 'admin', 'qa'].includes(role)) ||
      (resource.accessLevel === 'admin' && ['admin', 'qa'].includes(role));

    // External facilitators can only see resources assigned to their scope or explicitly assigned.
    if (isExternalFacilitator) {
      if (resource.availableAt) {
        const availableAt = resource.availableAt?.toDate
          ? resource.availableAt.toDate()
          : new Date(resource.availableAt);
        if (availableAt > new Date()) return false;
      }
      const cohortIds = Array.isArray(resource.cohortIds)
        ? resource.cohortIds
        : (resource.cohortId ? [resource.cohortId] : []);
      const assignedUsers = resource.assignedExternalUsers || [];
      const cohortMatch = userProfile?.assignedCohorts
        ? cohortIds.some((id) => userProfile.assignedCohorts.includes(id))
        : false;
      const assignedPrograms = userProfile?.assignedPrograms || [];
      const programMatch = assignedPrograms.length > 0
        ? assignedPrograms.some((programId) => resourceMatchesProgram(resource, programId))
        : false;
      const baseAccess = normalizedAccess === 'public' || normalizedAccess === 'all';
      hasAccess = (baseAccess && (cohortMatch || programMatch)) || assignedUsers.includes(currentUser?.uid);
    }

    return matchesSearch && matchesCategory && matchesType && matchesProgram && matchesCohort && hasAccess;
  });

  const filteredModules = useMemo(() => {
    if (!uploadFormData.programId) return modules;
    return modules.filter(module => module.programId === uploadFormData.programId);
  }, [modules, uploadFormData.programId]);

  const filteredCohorts = useMemo(() => {
    if (selectedProgram === 'all') return cohorts;
    return cohorts.filter(cohort => cohort.programId === selectedProgram);
  }, [cohorts, selectedProgram]);

  const categoryResourceCounts = useMemo(() => {
    return resourceCategories.reduce((acc, category) => {
      acc[category.value] = resources.filter(resource => resource.category === category.value).length;
      return acc;
    }, {});
  }, [resources]);

  const moduleNameMap = useMemo(() => {
    return modules.reduce((acc, module) => {
      acc[module.id] = module.moduleName || module.title || module.name || 'Untitled Module';
      return acc;
    }, {});
  }, [modules]);

  const getStructuredSectionKey = (resource) => {
    if (resource.category === 'videos' || resource.type === 'video' || resource.type === 'audio') {
      return 'recordings';
    }
    if (resource.category === 'assessments') {
      return 'assignments';
    }
    if (resource.category === 'presentations' || resource.type === 'presentation') {
      return 'presentations';
    }
    if (resource.category === 'templates' || resource.type === 'template') {
      return 'templates';
    }
    if (resource.category === 'links' || resource.category === 'tools' || resource.type === 'link' || resource.type === 'tool') {
      return 'links';
    }
    if (
      resource.category === 'documents' ||
      resource.category === 'research' ||
      resource.type === 'pdf' ||
      resource.type === 'document' ||
      resource.type === 'spreadsheet' ||
      resource.type === 'image'
    ) {
      return 'notes';
    }
    return 'other';
  };

  const structuredResourceGroups = useMemo(() => {
    const groups = new Map();

    filteredResources.forEach((resource) => {
      const moduleKey = resource.moduleId || 'general';
      const moduleLabel = resource.moduleId
        ? (moduleNameMap[resource.moduleId] || 'Untitled Module')
        : 'General Programme Resources';
      const sectionKey = getStructuredSectionKey(resource);

      if (!groups.has(moduleKey)) {
        groups.set(moduleKey, {
          moduleId: resource.moduleId || null,
          moduleLabel,
          totalResources: 0,
          sections: resourceSectionDefinitions.reduce((acc, section) => {
            acc[section.key] = [];
            return acc;
          }, {})
        });
      }

      const group = groups.get(moduleKey);
      group.sections[sectionKey].push(resource);
      group.totalResources += 1;
    });

    return Array.from(groups.values()).sort((a, b) => {
      if (a.moduleId === null) return 1;
      if (b.moduleId === null) return -1;
      return a.moduleLabel.localeCompare(b.moduleLabel);
    });
  }, [filteredResources, moduleNameMap]);

  const getAccessLabel = (accessLevel) => {
    switch (accessLevel) {
      case 'public': return 'Public';
      case 'all': return 'All users';
      case 'facilitators': return 'Facilitators';
      case 'admin': return 'Admin';
      default: return '';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'pdf': return 'PDF';
      case 'video': return 'Video';
      case 'audio': return 'Audio';
      case 'image': return 'Image';
      case 'document': return 'Document';
      case 'spreadsheet': return 'Spreadsheet';
      case 'presentation': return 'Presentation';
      case 'link': return 'Link';
      case 'tool': return 'Tool';
      case 'template': return 'Template';
      default: return type || 'Resource';
    }
  };

  const handleEditResource = (resource) => {
    try {
      const rawTemplateDueDate = resource.templateDueDate;
      let parsedTemplateDueDate = null;
      if (rawTemplateDueDate?.toDate) {
        parsedTemplateDueDate = rawTemplateDueDate.toDate();
      } else if (rawTemplateDueDate?.seconds) {
        parsedTemplateDueDate = new Date(rawTemplateDueDate.seconds * 1000);
      } else if (rawTemplateDueDate) {
        parsedTemplateDueDate = new Date(rawTemplateDueDate);
      }

      const templateDueDateValue = parsedTemplateDueDate && !Number.isNaN(parsedTemplateDueDate.getTime())
        ? parsedTemplateDueDate.toISOString().split('T')[0]
        : '';

      // Populate form with existing resource data
      const resolvedReleaseType = resource.releaseType === 'scheduled' ? 'scheduled' : 'immediate';
      setUploadFormData({
        title: resource.title || '',
        description: resource.description || '',
        category: resource.category || '',
        type: resource.type || '',
        tags: Array.isArray(resource.tags) ? resource.tags.join(', ') : (resource.tags || ''),
        accessLevel: resource.accessLevel || 'public',
        moduleId: resource.moduleId || '',
        releaseType: resolvedReleaseType,
        releaseMeetingId: resource.releaseMeetingId || '',
        releaseDelayMinutes: resource.releaseDelayMinutes ?? 5,
        releaseAt: resolvedReleaseType === 'scheduled' && resource.availableAt
          ? new Date(resource.availableAt?.toDate ? resource.availableAt.toDate() : resource.availableAt)
            .toISOString()
            .slice(0, 16)
          : '',
        file: null,
        url: resource.url || '',
        isExternalLink: resource.isExternalLink || false,
        isFillableTemplate: resource.isFillableTemplate || false,
        templateInstructions: resource.templateInstructions || '',
        templateDueDate: templateDueDateValue,
        programId: resource.programId || '',
        cohortId: resource.cohortId || '',
        cohortIds: Array.isArray(resource.cohortIds)
          ? resource.cohortIds
          : (resource.cohortId ? [resource.cohortId] : []),
        assignedExternalUsers: resource.assignedExternalUsers || []
      });
      setSelectedResource(resource);
      setShowUploadModal(true);
    } catch (error) {
      devError('Error editing resource:', error);
      showToastMessage('Error loading resource for editing: ' + error.message, 'error');
    }
  };

  const handleUpdateResource = async (e) => {
    e.preventDefault();

    if (!selectedResource) {
      return handleUploadSubmit(e); // If no selected resource, this is a new upload
    }

    try {
      if (!uploadFormData.title.trim()) {
        showToastMessage('Please enter a resource title', 'error');
        return;
      }

      if (uploadFormData.releaseType === 'scheduled' && !uploadFormData.releaseAt) {
        showToastMessage('Please choose a date/time for the release schedule', 'error');
        return;
      }

      setLoading(true);

      // Only handle file upload if a new file is selected
      let fileSize = selectedResource.fileSize;
      let storagePath = selectedResource.storagePath;

      if (!uploadFormData.isExternalLink && uploadFormData.file) {
        const file = uploadFormData.file;
        const maxFileSize = 1024 * 1024 * 1024; // 1GB
        if (file.size > maxFileSize) {
          showToastMessage('File is too large. Maximum file size is 1 GB.', 'error');
          return;
        }

        const { ref, uploadBytesResumable } = await import('firebase/storage');
        const { storage } = await import('../firebase');

        const fileName = `${Date.now()}_${file.name}`;
        storagePath = `lms-resources/${currentUser.uid}/${fileName}`;
        const storageRef = ref(storage, storagePath);

        setUploadingFile(true);
        setUploadProgress(0);
        const uploadTask = uploadBytesResumable(storageRef, file);

        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
              setUploadProgress(progress);
            },
            reject,
            resolve
          );
        });

        fileSize = formatFileSize(file.size);
      }

      const availableAt = computeAvailableAt();

      const resourceData = {
        title: uploadFormData.title,
        description: uploadFormData.description,
        category: uploadFormData.category,
        type: uploadFormData.type,
        tags: uploadFormData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        accessLevel: uploadFormData.accessLevel,
        moduleId: uploadFormData.moduleId || null,
        isExternalLink: uploadFormData.isExternalLink,
        url: uploadFormData.isExternalLink ? uploadFormData.url : null,
        fileURL: uploadFormData.isExternalLink || uploadFormData.file
          ? null
          : selectedResource.fileURL || null,
        fileSize: fileSize,
        storagePath: storagePath,
        updatedAt: serverTimestamp(),
        availableAt: availableAt ? Timestamp.fromDate(availableAt) : null,
        releaseType: uploadFormData.releaseType || 'immediate',
        releaseMeetingId: null,
        releaseDelayMinutes: null,
        // Program and cohort association
        programId: uploadFormData.programId || null,
        cohortIds: uploadFormData.cohortIds?.length ? uploadFormData.cohortIds : null,
        cohortId: uploadFormData.cohortIds?.length === 1 ? uploadFormData.cohortIds[0] : null,
        // External user assignment
        assignedExternalUsers: uploadFormData.assignedExternalUsers || [],
        isFillableTemplate: uploadFormData.isFillableTemplate || false,
        templateInstructions: uploadFormData.isFillableTemplate ? uploadFormData.templateInstructions : null,
        templateDueDate: uploadFormData.isFillableTemplate && uploadFormData.templateDueDate
          ? new Date(uploadFormData.templateDueDate)
          : null
      };

      await updateDoc(doc(db, 'resources', selectedResource.id), resourceData);

      showToastMessage('Resource updated successfully!', 'success');
      setShowUploadModal(false);
      setSelectedResource(null);
      resetUploadForm();
      loadResources();

    } catch (error) {
      devError('Error updating resource:', error);
      showToastMessage('Error updating resource: ' + error.message, 'error');
    } finally {
      setLoading(false);
      setUploadingFile(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      await deleteDoc(doc(db, 'resources', resourceId));
      showToastMessage('Resource deleted successfully!', 'success');
      loadResources();
    } catch (error) {
      devError('Error deleting resource:', error);
      showToastMessage('Error deleting resource: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for program and cohort names
  const getProgramName = (programId) => {
    if (!programId) return 'All Programs';
    const program = programs.find(p => p.id === programId);
    return program?.name || program?.programName || 'Unknown Program';
  };

  const getCohortName = (cohortId) => {
    if (!cohortId) return 'All Cohorts';
    const cohort = cohorts.find(c => c.id === cohortId);
    return cohort?.name || cohort?.cohortName || 'Unknown Cohort';
  };

  const getCohortNames = (resource) => {
    const ids = Array.isArray(resource.cohortIds)
      ? resource.cohortIds
      : (resource.cohortId ? [resource.cohortId] : []);
    if (ids.length === 0) return 'All Cohorts';
    return ids.map(id => getCohortName(id)).join(', ');
  };

  function resourceMatchesCohort(resource, cohortId) {
    const ids = Array.isArray(resource.cohortIds)
      ? resource.cohortIds
      : (resource.cohortId ? [resource.cohortId] : []);
    if (resource.category === 'templates') {
      return ids.length > 0 && ids.includes(cohortId);
    }
    if (ids.length === 0) return true;
    return ids.includes(cohortId);
  }

  const getExternalUserName = (userId) => {
    const user = externalUsers.find(u => u.id === userId);
    return user?.displayName || user?.email || 'Unknown User';
  };

  // Handle opening the assign modal for a resource
  const handleOpenAssignModal = (resource) => {
    setResourceToAssign(resource);
    setSelectedExternalUsers(resource.assignedExternalUsers || []);
    setShowAssignModal(true);
  };

  // Handle assigning resources to external users
  const handleAssignExternalUsers = async () => {
    if (!resourceToAssign) return;

    try {
      setLoading(true);
      await updateDoc(doc(db, 'resources', resourceToAssign.id), {
        assignedExternalUsers: selectedExternalUsers,
        updatedAt: serverTimestamp()
      });

      showToastMessage('External users assigned successfully!', 'success');
      setShowAssignModal(false);
      setResourceToAssign(null);
      setSelectedExternalUsers([]);
      loadResources();
    } catch (error) {
      devError('Error assigning external users:', error);
      showToastMessage('Error assigning external users: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Toggle external user selection
  const toggleExternalUserSelection = (userId) => {
    setSelectedExternalUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const renderResourceCard = (resource) => (
    <Card className="h-100 shadow-sm">
      <Card.Header className="d-flex justify-content-between align-items-start">
        <div>
          <Badge bg="secondary">{getTypeLabel(resource.type)}</Badge>
          {resource.category && <Badge bg="light" text="dark" className="ms-2">{resource.category}</Badge>}
        </div>
        <div>
          {resource.accessLevel && <Badge bg="light" text="dark" className="me-2">{getAccessLabel(resource.accessLevel)}</Badge>}
          {resource.version && (
            <Badge bg="outline-primary">v{resource.version}</Badge>
          )}
        </div>
      </Card.Header>
      <Card.Body className="d-flex flex-column">
        <h6 className="card-title">{resource.title}</h6>
        <p className="text-muted small flex-grow-1">
          {resource.description}
        </p>

        <div className="mb-2">
          {(resource.tags || []).map((tag, index) => (
            <Badge key={index} bg="light" text="dark" className="me-1 mb-1">
              {tag}
            </Badge>
          ))}
        </div>

        <div className="mb-2">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <small className="text-muted">
                Rating {Number(resource.rating || 0).toFixed(1)}
              </small>
            </div>
            <small className="text-muted">
              {resource.downloadCount} downloads
            </small>
          </div>
        </div>

        <div className="mt-auto">
          <Row>
            <Col>
              <Button
                variant="primary"
                size="sm"
                className="w-100"
                onClick={() => handleDownload(resource)}
                disabled={downloadingId === resource.id}
              >
                {downloadingId === resource.id ? (
                  <><Spinner animation="border" size="sm" className="me-1" />Loading...</>
                ) : resource.isExternalLink ? 'Visit' : 'Download'}
              </Button>
            </Col>
            <Col xs="auto">
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  ⋮
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => {
                    setSelectedResource(resource);
                    setShowResourceModal(true);
                  }}>
                    View Details
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleRateResource(resource.id, 5)}>
                    Rate 5 Stars
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleRateResource(resource.id, 4)}>
                    Rate 4 Stars
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleRateResource(resource.id, 3)}>
                    Rate 3 Stars
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>
          {role === 'learner' && resource.isFillableTemplate && (
            <Button
              variant="outline-success"
              size="sm"
              className="w-100 mt-2"
              onClick={() => openTemplateSubmitModal(resource)}
            >
              Submit Completed Template
            </Button>
          )}
        </div>
      </Card.Body>
      <Card.Footer className="text-muted">
        <small>
          By {resource.uploadedBy} • {resource.uploadedAt.toLocaleDateString()}
          {resource.fileSize && <> • {resource.fileSize}</>}
          {resource.duration && <> • {resource.duration}</>}
        </small>
      </Card.Footer>
    </Card>
  );

  return (
    <>
      {/* Navbar based on user role */}
      {role === 'learner' ? (
        <LearnerNavbar />
      ) : role === 'facilitator' ? (
        <FacilitatorNavbar />
      ) : role === 'admin' ? (
        <AdminNavbar />
      ) : role === 'qa' ? (
        <QANavbar />
      ) : null}

      {/* Toast Notification */}
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1050 }}>
        <Toast show={showToast} onClose={() => setShowToast(false)} bg={toastVariant}>
          <Toast.Header closeButton={false}>
            <strong className="me-auto">
              {toastVariant === 'success' ? 'Success' : toastVariant === 'warning' ? 'Warning' : toastVariant === 'info' ? 'Info' : 'Error'}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">Resource Hub</h1>
              <p className="mission-text mb-4">
                Learning materials, templates, recordings, and useful links for each program.
              </p>
              <p className="mb-0">
                Search the library, open approved resources, and manage materials for assigned cohorts.
              </p>
            </Col>
            <Col lg={4} className="d-flex align-items-center justify-content-end">
              {['facilitator', 'admin', 'qa'].includes(role) && (
                <div className="d-flex gap-2">
                  <Button variant="primary" onClick={() => setShowUploadModal(true)}>
                    Upload Resource
                  </Button>
                  <Button variant="outline-primary" onClick={() => setShowSubmissionRequestModal(true)}>
                    Request Submission
                  </Button>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">

      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-4">
        <Tab eventKey="browse" title="Browse Resources">
          {/* Search and Filter Bar */}
          <Row className="mb-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>Search</InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search resources..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                {resourceCategories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.icon} {cat.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
              >
                <option value="all">All Types</option>
                {resourceTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          {/* Program and Cohort Filters */}
          {role !== 'learner' && (
          <Row className="mb-4">
            <Col md={4}>
              <Form.Select
                value={selectedProgram}
                onChange={(e) => {
                  setSelectedProgram(e.target.value);
                  setSelectedCohort('all'); // Reset cohort when program changes
                }}
              >
                <option value="all">All Programs</option>
                {programs.map(prog => (
                  <option key={prog.id} value={prog.id}>
                    {prog.name || prog.programName}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Select
                value={selectedCohort}
                onChange={(e) => setSelectedCohort(e.target.value)}
              >
                <option value="all">All Cohorts</option>
                {filteredCohorts.map(cohort => (
                  <option key={cohort.id} value={cohort.id}>
                    {cohort.name || cohort.cohortName}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4} className="text-end">
              <Badge bg="info" className="p-2">{filteredResources.length} resources found</Badge>
              {(selectedProgram !== 'all' || selectedCohort !== 'all' || selectedCategory !== 'all' || selectedType !== 'all' || searchTerm) && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="ms-2"
                  onClick={() => {
                    setSelectedProgram('all');
                    setSelectedCohort('all');
                    setSelectedCategory('all');
                    setSelectedType('all');
                    setSearchTerm('');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Col>
          </Row>
          )}

          {/* Resources Grid */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2">Loading resources...</p>
            </div>
          ) : (
            <Row>
              {filteredResources.map(resource => (
                <Col md={6} lg={4} key={resource.id} className="mb-4">
                  {renderResourceCard(resource)}
                </Col>
              ))}

              {filteredResources.length === 0 && (
                <Col>
                  <Alert variant="light" className="text-center py-5">
                    <h5>No Resources Found</h5>
                    <p>Try adjusting your search criteria or browse different categories.</p>
                    {['facilitator', 'admin'].includes(role) && (
                      <Button variant="primary" onClick={() => setShowUploadModal(true)}>
                        Upload First Resource
                      </Button>
                    )}
                  </Alert>
                </Col>
              )}
            </Row>
          )}
        </Tab>

        <Tab eventKey="structured" title="Structured View">
          <Row className="mb-3">
            <Col>
              <Alert variant="info" className="mb-0">
                Resources are grouped by module and organised into recordings, assignments, notes, presentations, templates, and tools.
              </Alert>
            </Col>
          </Row>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2">Preparing structured resource view...</p>
            </div>
          ) : structuredResourceGroups.length === 0 ? (
            <Alert variant="light" className="text-center py-5">
              <h5>No Structured Resources Found</h5>
              <p className="mb-0">Try broadening the current filters or link resources to a module.</p>
            </Alert>
          ) : (
            structuredResourceGroups.map((group) => (
              <Card key={group.moduleId || 'general'} className="mb-4 shadow-sm">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-1">{group.moduleLabel}</h5>
                    <small className="text-muted">
                      {group.totalResources} resource{group.totalResources === 1 ? '' : 's'}
                    </small>
                  </div>
                  <Badge bg={group.moduleId ? 'primary' : 'secondary'}>
                    {group.moduleId ? 'Module-linked' : 'General'}
                  </Badge>
                </Card.Header>
                <Card.Body>
                  {resourceSectionDefinitions
                    .filter((section) => group.sections[section.key]?.length > 0)
                    .map((section) => (
                      <div key={section.key} className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0">{section.label}</h6>
                          <Badge bg="light" text="dark">
                            {group.sections[section.key].length}
                          </Badge>
                        </div>
                        <Table responsive hover size="sm" className="mb-0">
                          <thead>
                            <tr>
                              <th>Title</th>
                              <th>Type</th>
                              <th>Program / Cohort</th>
                              <th>Added</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.sections[section.key].map((resource) => (
                              <tr key={resource.id}>
                                <td>
                                  <div className="fw-semibold">{resource.title}</div>
                                  <small className="text-muted">{resource.description || 'No description provided'}</small>
                                </td>
                                <td>
                                  <Badge bg="secondary">{resource.type || resource.category}</Badge>
                                </td>
                                <td>
                                  <small>
                                    {getProgramName(resource.programId)}
                                    <br />
                                    {getCohortNames(resource)}
                                  </small>
                                </td>
                                <td>
                                  <small>{resource.uploadedAt.toLocaleDateString()}</small>
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleDownload(resource)}
                                      disabled={downloadingId === resource.id}
                                    >
                                      {downloadingId === resource.id ? (
                                        <><Spinner animation="border" size="sm" className="me-1" />Loading...</>
                                      ) : resource.isExternalLink ? 'Open' : 'Download'}
                                    </Button>
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedResource(resource);
                                        setShowResourceModal(true);
                                      }}
                                    >
                                      View
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ))}
                </Card.Body>
              </Card>
            ))
          )}
        </Tab>

        {['facilitator', 'admin', 'qa'].includes(role) && (
          <Tab eventKey="submission-requests" title="Submission Tracking">
            <Row className="mb-3">
              <Col md={4}>
                <Card className="h-100">
                  <Card.Header>
                    <strong>Submission Requests</strong>
                  </Card.Header>
                  <Card.Body style={{ maxHeight: 420, overflowY: 'auto' }}>
                    {loadingSubmissionRequests ? (
                      <div className="text-center py-3">
                        <Spinner animation="border" size="sm" />
                      </div>
                    ) : submissionRequestsList.length === 0 ? (
                      <Alert variant="light" className="mb-0">
                        No submission requests yet.
                      </Alert>
                    ) : (
                      <div className="list-group">
                        {submissionRequestsList.map((request) => {
                          const isActive = selectedSubmissionRequest?.id === request.id;
                          const createdAt = request.createdAt?.toDate
                            ? request.createdAt.toDate()
                            : (request.createdAt ? new Date(request.createdAt) : null);
                          return (
                            <button
                              key={request.id}
                              type="button"
                              className={`list-group-item list-group-item-action ${isActive ? 'active' : ''}`}
                              onClick={() => setSelectedSubmissionRequest(request)}
                            >
                              <div className="fw-semibold">{request.title}</div>
                              <div className="small text-muted">
                                Due: {request.dueDate?.toDate ? request.dueDate.toDate().toLocaleDateString() : (request.dueDate ? new Date(request.dueDate).toLocaleDateString() : 'N/A')}
                              </div>
                              <div className="small text-muted">
                                Created: {createdAt ? createdAt.toLocaleDateString() : 'N/A'}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col md={8}>
                <Card className="h-100">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <strong>Submissions</strong>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => selectedSubmissionRequest?.id && loadSubmissionSubmissions(selectedSubmissionRequest.id)}
                        disabled={!selectedSubmissionRequest}
                      >
                        Refresh
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={handleBulkDownloadSubmissionFiles}
                        disabled={!selectedSubmissionRequest || submissionRequestSubmissions.length === 0}
                      >
                        Bulk Download Files
                      </Button>
                      {selectedSubmissionRequest && (
                        (selectedSubmissionRequest.createdById === currentUser?.uid ||
                         role === 'admin' ||
                         role === 'qa') && (
                          <>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={openSubmissionEditModal}
                            >
                              Edit Availability
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={handleDeleteSubmissionRequest}
                            >
                              Delete
                            </Button>
                          </>
                        )
                      )}
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {!selectedSubmissionRequest ? (
                      <Alert variant="light" className="mb-0">
                        Select a request to view submissions.
                      </Alert>
                    ) : loadingSubmissionSubmissions ? (
                      <div className="text-center py-3">
                        <Spinner animation="border" size="sm" />
                      </div>
                    ) : submissionRequestSubmissions.length === 0 ? (
                      <Alert variant="light" className="mb-0">
                        No submissions yet for this request.
                      </Alert>
                    ) : (
                      <Table responsive size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>Learner</th>
                            <th>Email</th>
                            <th>Submitted</th>
                            <th>File</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {submissionRequestSubmissions.map((submission) => (
                            <tr key={submission.id}>
                              <td>{submission.learnerName || 'Learner'}</td>
                              <td>{submission.learnerEmail || ''}</td>
                              <td>{submission.submittedAt ? submission.submittedAt.toLocaleString() : 'N/A'}</td>
                              <td>
                                {Array.isArray(submission.files) && submission.files.length > 0 ? (
                                  <div className="d-flex flex-column gap-1">
                                    {submission.files.map((file, index) => (
                                      <Button
                                        key={`${submission.id}-file-${index}`}
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={() => openSubmissionFilePreview(file.fileUrl, file.fileName)}
                                      >
                                        {file.fileName || `File ${index + 1}`}
                                      </Button>
                                    ))}
                                  </div>
                                ) : submission.fileUrl ? (
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => openSubmissionFilePreview(submission.fileUrl, submission.fileName)}
                                  >
                                    View
                                  </Button>
                                ) : (
                                  'N/A'
                                )}
                              </td>
                              <td>{submission.status || 'submitted'}</td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => openSubmissionStatusModal(submission)}
                                  >
                                    Update
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-warning"
                                    onClick={() => openResubmissionRequestModal(submission)}
                                    disabled={(submission.status || '').toLowerCase().replace(/\s+/g, '_') === 'revision_requested'}
                                  >
                                    Request Resubmission
                                  </Button>
                                  {(role === 'admin' || role === 'facilitator') && (
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => handleDeleteSubmission(submission.id)}
                                    >
                                      Delete
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            {selectedSubmissionRequest && (
              <Row>
                <Col>
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <strong>Missing Submissions</strong>
                      <div className="d-flex gap-2 align-items-center">
                        <Badge bg="secondary">
                          {submissionMissingLearners.length} missing / {submissionRequestLearners.length} total
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          disabled={submissionMissingLearners.length === 0}
                          onClick={handleSendSubmissionReminder}
                        >
                          Send Reminder
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      {submissionMissingLearners.length === 0 ? (
                        <Alert variant="light" className="mb-0">
                          All learners have submitted.
                        </Alert>
                      ) : (
                        <Table responsive size="sm" className="mb-0">
                          <thead>
                            <tr>
                              <th>Learner</th>
                              <th>Email</th>
                              <th>Cohort</th>
                            </tr>
                          </thead>
                          <tbody>
                            {submissionMissingLearners.map((learner) => (
                              <tr key={learner.id}>
                                <td>{learner.displayName}</td>
                                <td>{learner.email}</td>
                                <td>{getCohortName(learner.cohortId) || 'Unknown Cohort'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </Tab>
        )}

        <Tab eventKey="categories" title="Categories">
          <Row>
            {resourceCategories.map(category => {
              return (
                <Col md={6} lg={4} key={category.value} className="mb-4">
                  <Card className="h-100 text-center">
                    <Card.Body>
                      <div style={{ fontSize: '3rem' }}>{category.icon}</div>
                      <h5 className="mt-3">{category.label}</h5>
                      <p className="text-muted">
                        {categoryResourceCounts[category.value] || 0} resources available
                      </p>
                      <Button
                        variant="outline-primary"
                        onClick={() => {
                          setSelectedCategory(category.value);
                          setActiveTab('browse');
                        }}
                      >
                        Browse {category.label}
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Tab>

        <Tab eventKey="teams-meetings" title="Teams Meetings">
          <Row>
            <Col lg={12}>
              <GoogleMeetLinks />
            </Col>
          </Row>
        </Tab>

        {['facilitator', 'admin', 'qa'].includes(role) && (
          <Tab eventKey="manage" title="Manage Resources">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Resource Management</h5>
                {role === 'admin' && externalUsers.length > 0 && (
                  <Badge bg="info">{externalUsers.length} external users available for assignment</Badge>
                )}
              </Card.Header>
              <Card.Body>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Resource</th>
                      <th>Category</th>
                      <th>Program/Cohort</th>
                      <th>Access Level</th>
                      {role === 'admin' && <th>External Users</th>}
                      <th>Downloads</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resources.map(resource => (
                      <tr key={resource.id}>
                        <td>
                          <div>
                            <strong>{resource.title}</strong>
                            <br />
                            <small className="text-muted">
                              {resource.uploadedBy} • {resource.uploadedAt.toLocaleDateString()}
                            </small>
                          </div>
                        </td>
                        <td>
                          <Badge bg="secondary">{resource.category}</Badge>
                        </td>
                        <td>
                          <div>
                            <small>
                              <strong>Program:</strong> {getProgramName(resource.programId)}
                            </small>
                            <br />
                            <small>
                              <strong>Cohort:</strong> {getCohortNames(resource)}
                            </small>
                          </div>
                        </td>
                        <td>
                          <Badge bg={(resource.accessLevel === 'public' || resource.accessLevel === 'all' || !resource.accessLevel)
                            ? 'success'
                            : resource.accessLevel === 'facilitators' ? 'warning' : 'danger'}>
                            {resource.accessLevel || 'public'}
                          </Badge>
                        </td>
                        {role === 'admin' && (
                          <td>
                            <div>
                              <Badge bg={(resource.assignedExternalUsers || []).length > 0 ? 'primary' : 'secondary'}>
                                {(resource.assignedExternalUsers || []).length} assigned
                              </Badge>
                              {(resource.assignedExternalUsers || []).length > 0 && (
                                <div className="mt-1">
                                  <small className="text-muted">
                                    {(resource.assignedExternalUsers || []).slice(0, 2).map(uid => (
                                      <span key={uid} className="d-block">{getExternalUserName(uid)}</span>
                                    ))}
                                    {(resource.assignedExternalUsers || []).length > 2 && (
                                      <span>+{(resource.assignedExternalUsers || []).length - 2} more</span>
                                    )}
                                  </small>
                                </div>
                              )}
                            </div>
                          </td>
                        )}
                        <td>{resource.downloadCount}</td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditResource(resource)}
                            >
                              Edit
                            </Button>
                            {resource.isFillableTemplate && (
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => setTrackingResource(resource)}
                              >
                                Track Submissions
                              </Button>
                            )}
                            {role === 'admin' && (
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => handleOpenAssignModal(resource)}
                              >
                                Assign External
                              </Button>
                            )}
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteResource(resource.id)}
                            >
                              Remove
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>
        )}

        {['facilitator', 'admin', 'qa'].includes(role) && (
          <Tab eventKey="review-templates" title="Review Templates">
            <TemplateReviewPanel />
          </Tab>
        )}
      </Tabs>

      {/* Template Submission Tracking Modal */}
      <Modal show={Boolean(trackingResource)} onHide={() => setTrackingResource(null)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Template Submission Tracking</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {trackingResource ? (
            <TemplateSubmissionTracker template={trackingResource} />
          ) : (
            <Alert variant="info" className="mb-0">
              Select a template to track submissions.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setTrackingResource(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Upload Resource Modal */}
      <Modal show={showUploadModal} onHide={() => {
        setShowUploadModal(false);
        setSelectedResource(null);
        resetUploadForm();
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedResource ? 'Edit Resource' : 'Upload Resource'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={selectedResource ? handleUpdateResource : handleUploadSubmit}>
          <Modal.Body>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Resource Title *</Form.Label>
                  <Form.Control
                    type="text"
                    value={uploadFormData.title}
                    onChange={(e) => setUploadFormData({ ...uploadFormData, title: e.target.value })}
                    placeholder="Enter resource title"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Category *</Form.Label>
                  <Form.Select
                    value={uploadFormData.category}
                    onChange={(e) => setUploadFormData({ ...uploadFormData, category: e.target.value })}
                    required
                  >
                    {resourceCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.icon} {cat.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={uploadFormData.description}
                onChange={(e) => setUploadFormData({ ...uploadFormData, description: e.target.value })}
                placeholder="Describe the resource and its purpose"
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Type *</Form.Label>
                  <Form.Select
                    value={uploadFormData.type}
                    onChange={(e) => setUploadFormData({ ...uploadFormData, type: e.target.value })}
                    required
                  >
                    {resourceTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Access Level *</Form.Label>
                  <Form.Select
                    value={uploadFormData.accessLevel}
                    onChange={(e) => setUploadFormData({ ...uploadFormData, accessLevel: e.target.value })}
                    required
                  >
                    <option value="public">Public (All Users)</option>
                    <option value="facilitators">Facilitators Only</option>
                    {role === 'admin' && (
                      <option value="admin">Admin Only</option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="External Link"
                    checked={uploadFormData.isExternalLink}
                    onChange={(e) => setUploadFormData({ ...uploadFormData, isExternalLink: e.target.checked })}
                    className="mt-4"
                  />
                </Form.Group>
              </Col>
            </Row>

            {uploadFormData.isExternalLink ? (
              <Form.Group className="mb-3">
                <Form.Label>URL *</Form.Label>
                <Form.Control
                  type="url"
                  value={uploadFormData.url}
                  onChange={(e) => setUploadFormData({ ...uploadFormData, url: e.target.value })}
                  placeholder="https://example.com/resource"
                  required
                />
              </Form.Group>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>File *</Form.Label>
                <Form.Control
                  type="file"
                  onChange={(e) => setUploadFormData({ ...uploadFormData, file: e.target.files[0] })}
                  required={!uploadFormData.isExternalLink && !selectedResource}
                />
                <Form.Text className="text-muted">
                  Maximum file size: 1 GB. Supported formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, MP4, MOV, AVI, WMV, WebM, MP3, WAV, images
                </Form.Text>
                {uploadingFile && (
                  <div className="mt-2">
                    <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
                  </div>
                )}
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Tags</Form.Label>
              <Form.Control
                type="text"
                value={uploadFormData.tags}
                onChange={(e) => setUploadFormData({ ...uploadFormData, tags: e.target.value })}
                placeholder="e.g., bee, compliance, assessment (comma-separated)"
              />
              <Form.Text className="text-muted">
                Add relevant tags to help users find this resource
              </Form.Text>
            </Form.Group>

            <hr />

            {/* Program and Cohort Association */}
            <h6 className="mb-3">Program & Cohort Association (Optional)</h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Program</Form.Label>
                <Form.Select
                    value={uploadFormData.programId}
                    onChange={(e) => setUploadFormData({
                      ...uploadFormData,
                      programId: e.target.value,
                      cohortId: '', // Reset cohort when program changes
                      cohortIds: []
                    })}
                  >
                    <option value="">All Programs</option>
                    {programs.map(prog => (
                      <option key={prog.id} value={prog.id}>
                        {prog.name || prog.programName}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Leave blank to make available to all programs
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cohorts</Form.Label>
                  <div className="border rounded p-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                    {(uploadFormData.programId
                      ? cohorts.filter(c => c.programId === uploadFormData.programId)
                      : cohorts
                    ).map(cohort => (
                      <Form.Check
                        key={cohort.id}
                        type="checkbox"
                        id={`cohort-${cohort.id}`}
                        label={cohort.name || cohort.cohortName}
                        checked={uploadFormData.cohortIds.includes(cohort.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setUploadFormData((prev) => ({
                            ...prev,
                            cohortIds: checked
                              ? [...prev.cohortIds, cohort.id]
                              : prev.cohortIds.filter(id => id !== cohort.id)
                          }));
                        }}
                      />
                    ))}
                    {cohorts.length === 0 && (
                      <small className="text-muted">No cohorts available.</small>
                    )}
                  </div>
                  <Form.Text className="text-muted">
                    Leave all unchecked to make available to all cohorts
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Module (Optional)</Form.Label>
                  <Form.Select
                    value={uploadFormData.moduleId}
                    onChange={(e) => setUploadFormData({ ...uploadFormData, moduleId: e.target.value })}
                  >
                    <option value="">All Modules</option>
                    {filteredModules.map(module => (
                      <option key={module.id} value={module.id}>
                        {module.moduleName || module.title || 'Untitled Module'}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Tie this resource to a specific module if needed.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <hr />
            <h6 className="mb-3">Learner Availability</h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Release Timing</Form.Label>
                  <Form.Select
                    value={uploadFormData.releaseType}
                    onChange={(e) => setUploadFormData({
                      ...uploadFormData,
                      releaseType: e.target.value,
                      releaseMeetingId: '',
                      releaseAt: ''
                    })}
                  >
                    <option value="immediate">Immediately</option>
                    <option value="scheduled">Schedule Date/Time</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Control when learners can access this resource.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                {uploadFormData.releaseType === 'scheduled' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Available At</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={uploadFormData.releaseAt}
                      onChange={(e) => setUploadFormData({
                        ...uploadFormData,
                        releaseAt: e.target.value
                      })}
                    />
                  </Form.Group>
                )}
              </Col>
            </Row>

            {/* External User Assignment (Admin Only) */}
            {role === 'admin' && externalUsers.length > 0 && (
              <>
                <hr />
                <h6 className="mb-3">Assign to External Users</h6>
                <Alert variant="info" className="mb-3">
                  <small>
                    Select external users who should have access to this resource regardless of its access level.
                  </small>
                </Alert>
                <div className="border rounded p-3 mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {externalUsers.map(user => (
                    <Form.Check
                      key={user.id}
                      type="checkbox"
                      id={`external-user-${user.id}`}
                      label={
                        <span>
                          {user.displayName || user.email}
                          <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.7rem' }}>
                            {user.role}
                          </Badge>
                        </span>
                      }
                      checked={(uploadFormData.assignedExternalUsers || []).includes(user.id)}
                      onChange={(e) => {
                        const currentUsers = uploadFormData.assignedExternalUsers || [];
                        if (e.target.checked) {
                          setUploadFormData({
                            ...uploadFormData,
                            assignedExternalUsers: [...currentUsers, user.id]
                          });
                        } else {
                          setUploadFormData({
                            ...uploadFormData,
                            assignedExternalUsers: currentUsers.filter(id => id !== user.id)
                          });
                        }
                      }}
                      className="mb-2"
                    />
                  ))}
                </div>
                {(uploadFormData.assignedExternalUsers || []).length > 0 && (
                  <Badge bg="primary">
                    {(uploadFormData.assignedExternalUsers || []).length} external user(s) selected
                  </Badge>
                )}
              </>
            )}

            <hr />

            {/* Fillable Template Options */}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="fillableTemplateCheck"
                label="This is a fillable template (learners can download, complete, and re-upload)"
                checked={uploadFormData.isFillableTemplate}
                onChange={(e) => setUploadFormData({ ...uploadFormData, isFillableTemplate: e.target.checked })}
              />
              <Form.Text className="text-muted">
                Enable this for blank forms, worksheets, or documents that learners need to fill in and submit
              </Form.Text>
            </Form.Group>

            {uploadFormData.isFillableTemplate && (
              <>
                <Alert variant="info" className="mb-3">
                  <strong>Perfect Copy Workflow:</strong> Learners will be able to download this template,
                  fill it in, and re-upload their completed version. You can review and grade their submissions.
                </Alert>

                <Form.Group className="mb-3">
                  <Form.Label>Instructions for Learners</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={uploadFormData.templateInstructions}
                    onChange={(e) => setUploadFormData({ ...uploadFormData, templateInstructions: e.target.value })}
                    placeholder="Provide clear instructions on how learners should complete this template..."
                  />
                  <Form.Text className="text-muted">
                    Explain what information to fill in, formatting requirements, etc.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Due Date (Optional)</Form.Label>
                  <Form.Control
                    type="date"
                    value={uploadFormData.templateDueDate}
                    onChange={(e) => setUploadFormData({ ...uploadFormData, templateDueDate: e.target.value })}
                  />
                  <Form.Text className="text-muted">
                    Set a deadline for learners to submit their completed templates
                  </Form.Text>
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowUploadModal(false);
              setSelectedResource(null);
              resetUploadForm();
            }}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading || uploadingFile}>
              {loading || uploadingFile ? <Spinner animation="border" size="sm" className="me-2" /> : ''}
              {selectedResource ? 'Update Resource' : 'Upload Resource'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Submission Request Modal */}
      <Modal
        show={showSubmissionRequestModal}
        onHide={() => setShowSubmissionRequestModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Submission Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={submissionRequestForm.title}
              onChange={(e) => setSubmissionRequestForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Upload signed attendance register"
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Due Date</Form.Label>
                <Form.Control
                  type="date"
                  value={submissionRequestForm.dueDate}
                  onChange={(e) => setSubmissionRequestForm(prev => ({ ...prev, dueDate: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Required File Type</Form.Label>
                <Form.Select
                  value={submissionRequestForm.requiredFileType}
                  onChange={(e) => setSubmissionRequestForm(prev => ({ ...prev, requiredFileType: e.target.value }))}
                >
                  <option value="pdf">PDF</option>
                  <option value="doc">DOC/DOCX</option>
                  <option value="xls">XLS/XLSX</option>
                  <option value="image">Image (JPG/PNG)</option>
                  <option value="any">Any file type</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="submission-request-multi"
                  label="Allow multiple files"
                  checked={submissionRequestForm.allowMultipleFiles}
                  onChange={(e) => setSubmissionRequestForm(prev => ({
                    ...prev,
                    allowMultipleFiles: e.target.checked,
                    maxFiles: e.target.checked ? prev.maxFiles : '1'
                  }))}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Max files (optional)</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={submissionRequestForm.maxFiles}
                  onChange={(e) => setSubmissionRequestForm(prev => ({ ...prev, maxFiles: e.target.value }))}
                  disabled={!submissionRequestForm.allowMultipleFiles}
                  placeholder="e.g. 3"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Program (Optional)</Form.Label>
            <Form.Select
              value={submissionRequestProgram}
              onChange={(e) => {
                setSubmissionRequestProgram(e.target.value);
                setSubmissionRequestCohorts([]);
              }}
            >
              <option value="">All Programs</option>
              {programs.map((prog) => (
                <option key={prog.id} value={prog.id}>
                  {prog.name || prog.programName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Label>Target Cohorts</Form.Label>
            <div className="border rounded p-2" style={{ maxHeight: 240, overflowY: 'auto' }}>
              {(submissionRequestProgram
                ? cohorts.filter(c => c.programId === submissionRequestProgram)
                : cohorts
              ).map((cohort) => (
                <Form.Check
                  key={`submission-request-cohort-${cohort.id}`}
                  type="checkbox"
                  id={`submission-request-cohort-${cohort.id}`}
                  label={cohort.name || cohort.cohortName}
                  checked={submissionRequestCohorts.includes(cohort.id)}
                  onChange={() => toggleSubmissionRequestCohort(cohort.id)}
                />
              ))}
              {(submissionRequestProgram
                ? cohorts.filter(c => c.programId === submissionRequestProgram)
                : cohorts
              ).length === 0 && (
                <div className="text-muted small">No cohorts available.</div>
              )}
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSubmissionRequestModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateSubmissionRequest}>
            Create Request
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Submission Request Modal */}
      <Modal
        show={showSubmissionEditModal}
        onHide={() => setShowSubmissionEditModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Availability</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Due Date</Form.Label>
            <Form.Control
              type="date"
              value={submissionEditForm.dueDate}
              onChange={(e) => setSubmissionEditForm(prev => ({ ...prev, dueDate: e.target.value }))}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={submissionEditForm.status}
              onChange={(e) => setSubmissionEditForm(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </Form.Select>
          </Form.Group>
          <Row className="mt-3">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="submission-edit-multi"
                  label="Allow multiple files"
                  checked={submissionEditForm.allowMultipleFiles}
                  onChange={(e) => setSubmissionEditForm(prev => ({
                    ...prev,
                    allowMultipleFiles: e.target.checked,
                    maxFiles: e.target.checked ? prev.maxFiles : '1'
                  }))}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Max files (optional)</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={submissionEditForm.maxFiles}
                  onChange={(e) => setSubmissionEditForm(prev => ({ ...prev, maxFiles: e.target.value }))}
                  disabled={!submissionEditForm.allowMultipleFiles}
                  placeholder="e.g. 3"
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSubmissionEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateSubmissionRequest}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Update Submission Status Modal */}
      <Modal
        show={showSubmissionStatusModal}
        onHide={() => setShowSubmissionStatusModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Update Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={submissionStatusForm.status}
              onChange={(e) => setSubmissionStatusForm(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="revision_requested">Revision Requested</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Comment</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={submissionStatusForm.comment}
              onChange={(e) => setSubmissionStatusForm(prev => ({ ...prev, comment: e.target.value }))}
              placeholder="Add feedback or notes..."
            />
          </Form.Group>
          {selectedSubmission?.reviewHistory?.length > 0 && (
            <div>
              <h6 className="mb-2">Previous Updates</h6>
              <ul className="small text-muted">
                {selectedSubmission.reviewHistory.map((entry, idx) => (
                  <li key={`history-${idx}`}>
                    {entry.status} — {entry.comment || 'No comment'}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSubmissionStatusModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateSubmissionStatus}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Resource Details Modal */}
      <Modal show={showResourceModal} onHide={() => setShowResourceModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Resource Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedResource && (
            <div>
              <Row>
                <Col md={8}>
                  <h5>{selectedResource.title}</h5>
                  <p className="text-muted">{selectedResource.description}</p>
                </Col>
                <Col md={4} className="text-end">
                  <div className="mb-2">
                    <div><small>Rating {Number(selectedResource.rating || 0).toFixed(1)}</small></div>
                    <div><small>{selectedResource.ratingCount || 0} ratings</small></div>
                  </div>
                  <Badge bg="secondary" className="me-2">{selectedResource.category}</Badge>
                  <Badge bg="info">{selectedResource.type}</Badge>
                </Col>
              </Row>

              <hr />

              <Row>
                <Col md={6}>
                  <h6>Statistics</h6>
                  <ul className="list-unstyled">
                    <li><strong>Downloads:</strong> {selectedResource.downloadCount}</li>
                    <li><strong>Views:</strong> {selectedResource.views || 'N/A'}</li>
                    <li><strong>File Size:</strong> {selectedResource.fileSize || 'N/A'}</li>
                    <li><strong>Version:</strong> {selectedResource.version || '1.0'}</li>
                  </ul>
                </Col>
                <Col md={6}>
                  <h6>Details</h6>
                  <ul className="list-unstyled">
                    <li><strong>Uploaded By:</strong> {selectedResource.uploadedBy}</li>
                    <li><strong>Upload Date:</strong> {selectedResource.uploadedAt.toLocaleDateString()}</li>
                    <li><strong>Access Level:</strong> {selectedResource.accessLevel}</li>
                    <li><strong>Status:</strong> Active</li>
                  </ul>
                </Col>
              </Row>

              {(selectedResource.tags || []).length > 0 && (
                <div>
                  <h6>Tags</h6>
                  <div>
                    {(selectedResource.tags || []).map((tag, index) => (
                      <Badge key={index} bg="light" text="dark" className="me-1 mb-1">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResourceModal(false)}>
            Close
          </Button>
          {selectedResource && role === 'learner' && selectedResource.isFillableTemplate && (
            <Button
              variant="outline-success"
              onClick={() => {
                setShowResourceModal(false);
                openTemplateSubmitModal(selectedResource);
              }}
            >
              Submit Completed Template
            </Button>
          )}
          {selectedResource && (
            <Button
              variant="primary"
              onClick={() => {
                handleDownload(selectedResource);
                setShowResourceModal(false);
              }}
            >
              {selectedResource.isExternalLink ? 'Visit Resource' : 'Download'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Submit Completed Template Modal (Learners) */}
      <Modal show={showTemplateSubmitModal} onHide={() => setShowTemplateSubmitModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Submit Completed Template</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleTemplateSubmit}>
          <Modal.Body>
            {templateSubmitResource && (
              <>
                <h5>{templateSubmitResource.title}</h5>
                {templateSubmitResource.templateInstructions && (
                  <Alert variant="info">
                    <strong>Instructions:</strong><br />
                    {templateSubmitResource.templateInstructions}
                  </Alert>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Upload Completed Template *</Form.Label>
                  <Form.Control
                    type="file"
                    required
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                    onChange={(e) => setTemplateSubmitFile(e.target.files[0])}
                  />
                  <Form.Text className="text-muted">
                    Maximum file size: 10MB. Accepted formats: PDF, Word, Excel, PowerPoint
                  </Form.Text>
                </Form.Group>

                {templateSubmitFile && (
                  <Alert variant="success">
                    <strong>Selected:</strong> {templateSubmitFile.name} ({(templateSubmitFile.size / 1024 / 1024).toFixed(2)} MB)
                  </Alert>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Comments (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={templateSubmitComments}
                    onChange={(e) => setTemplateSubmitComments(e.target.value)}
                    placeholder="Add any comments or notes for your facilitator..."
                  />
                </Form.Group>

                {templateSubmitting && (
                  <ProgressBar
                    now={templateSubmitProgress}
                    label={`${Math.round(templateSubmitProgress)}%`}
                    animated
                    className="mb-3"
                  />
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTemplateSubmitModal(false)} disabled={templateSubmitting}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={templateSubmitting || !templateSubmitFile}>
              {templateSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Uploading...
                </>
              ) : (
                'Submit Template'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Assign to External Users Modal (Admin Only) */}
      <Modal show={showAssignModal} onHide={() => {
        setShowAssignModal(false);
        setResourceToAssign(null);
        setSelectedExternalUsers([]);
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Assign Resource to External Users</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {resourceToAssign && (
            <>
              <Alert variant="info">
                <strong>Resource:</strong> {resourceToAssign.title}
                <br />
                <small className="text-muted">
                  Select external users who should have access to this resource.
                </small>
              </Alert>

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">External Users</h6>
                  <div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => setSelectedExternalUsers(externalUsers.map(u => u.id))}
                    >
                      Select All
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setSelectedExternalUsers([])}
                    >
                      Clear All
                    </Button>
                  </div>
                </div>

                {externalUsers.length === 0 ? (
                  <Alert variant="warning">
                    No external users found. Create external users first in User Management.
                  </Alert>
                ) : (
                  <div className="border rounded p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {externalUsers.map(user => (
                      <Card key={user.id} className="mb-2">
                        <Card.Body className="py-2">
                          <Form.Check
                            type="checkbox"
                            id={`assign-external-${user.id}`}
                            checked={selectedExternalUsers.includes(user.id)}
                            onChange={() => toggleExternalUserSelection(user.id)}
                            label={
                              <div className="d-flex justify-content-between align-items-center w-100">
                                <div>
                                  <strong>{user.displayName || 'No Name'}</strong>
                                  <br />
                                  <small className="text-muted">{user.email}</small>
                                </div>
                                <div className="text-end">
                                  <Badge bg={
                                    user.role === 'external_facilitator' ? 'primary' :
                                    user.role === 'external_viewer' ? 'info' : 'secondary'
                                  }>
                                    {user.role === 'external_facilitator' ? 'Ext. Facilitator' :
                                     user.role === 'external_viewer' ? 'Ext. Viewer' : 'External'}
                                  </Badge>
                                  {user.organization && (
                                    <div>
                                      <small className="text-muted">{user.organization}</small>
                                    </div>
                                  )}
                                </div>
                              </div>
                            }
                          />
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                )}

                <div className="mt-3">
                  <Badge bg="primary" className="me-2">
                    {selectedExternalUsers.length} user(s) selected
                  </Badge>
                  {resourceToAssign.assignedExternalUsers?.length > 0 && (
                    <Badge bg="secondary">
                      Currently assigned: {resourceToAssign.assignedExternalUsers.length}
                    </Badge>
                  )}
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowAssignModal(false);
            setResourceToAssign(null);
            setSelectedExternalUsers([]);
          }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssignExternalUsers}
            disabled={loading}
          >
            {loading ? <Spinner animation="border" size="sm" className="me-2" /> : ''}
            Save Assignments
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </>
  );
};

export default ResourceHub;
